/**
 * Shared press instrumentation for button primitives, so every button in the
 * app emits a `button_pressed` event without per-screen wiring. Captured on
 * the primitive (not autocapture) because RN Web's touch autocapture is
 * unreliable — it reached 6 users in the week before this existed.
 *
 * Events carry the primitive name plus the button's visible label, and
 * inherit `$screen_name` from the navigation super property registered in
 * `App.tsx`. No press payloads or form values are ever captured here.
 *
 * `label` is what a human reads on screen, so it moves with the locale and
 * with copy edits. `action` is the stable identifier a funnel is built on —
 * pass it wherever the label is translated or interpolated, using the i18n
 * key of the copy (keys are already stable semantic names). Without it a
 * funnel splits into one branch per language.
 */
import { useCallback } from "react";
import { usePostHog } from "posthog-react-native";
import { pressLabel } from "./pressLabel";

export { pressLabel };

export function usePressCapture(component: string) {
  // usePostHog is typed non-null but resolves to undefined outside a
  // PostHogProvider (tests, the design-system app) — keep every call
  // optional-chained so primitives stay renderable everywhere.
  const posthog = usePostHog();

  return useCallback(
    /**
     * @param label The control's real visible copy, via `pressLabel`. A
     *   static snake_case descriptor is only for a control that has no
     *   visible copy at all (an icon button, a backdrop, a dismiss target).
     * @param action Stable identifier for the control, required whenever
     *   `label` is translated or interpolated. Prefer the i18n key.
     */
    (label: string | null, action?: string | null) => {
      posthog?.capture("button_pressed", {
        component,
        ...(label ? { label } : {}),
        ...(action ? { action } : {}),
      });
    },
    [posthog, component],
  );
}
